import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import type { SelectChangeEvent } from '@mui/material'
import { Stack } from '@mui/material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import type { PickerOwnerState } from '@mui/x-date-pickers-pro'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDateRangePicker from '@/components/inputs/CustomDateRangePicker'
import CustomFormControl from '@/components/inputs/CustomFormControl'
import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import type { InsightRevenueDateTimeRange } from '@/features/site/types/revenue'

export type Period = 'year' | 'custom'

type TimeRangeFilterPropsByPeriodProps = {
  period: Period
  timeRange: InsightRevenueDateTimeRange
  timezone: string
  limitDateTimeRange: InsightRevenueDateTimeRange
  onChange: (timeRange: InsightRevenueDateTimeRange) => void
}

type SiteRevenuePeriodFilterProps = {
  defaultPeriod: Period
  timeRange: InsightRevenueDateTimeRange
  timezone: string
  limitDateTimeRange: InsightRevenueDateTimeRange
  onChange: (timeRange: InsightRevenueDateTimeRange) => void
}

type YearPickerIconProps = {
  ownerState: PickerOwnerState
}

const YearPickerIcon = ({ ownerState }: YearPickerIconProps) => {
  return ownerState.isPickerOpen ? <ArrowDropUp /> : <ArrowDropDown />
}

const TimeRangeFilterByPeriod = ({
  period,
  timeRange,
  onChange,
  timezone,
  limitDateTimeRange,
}: Readonly<TimeRangeFilterPropsByPeriodProps>) => {
  const { t, i18n } = useTranslation()

  switch (period) {
    case 'year':
      return (
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <CustomFormControl id="revenue-period-year" variant="outlinedWhite">
            <DesktopDatePicker
              disableFuture
              enableAccessibleFieldDOMStructure={false}
              label={t('common.period.year')}
              maxDate={limitDateTimeRange.endTime}
              shouldDisableYear={(year) => year < limitDateTimeRange.startTime.startOf('year')}
              slotProps={{
                desktopPaper: {
                  sx: { paddingY: 5, paddingX: 4.5 },
                },
                layout: {
                  sx: {
                    '& .MuiDateCalendar-root': {
                      height: 'auto',
                    },
                  },
                },
              }}
              slots={{
                openPickerIcon: YearPickerIcon,
              }}
              sx={{ width: '100px' }}
              value={timeRange.endTime}
              views={['year']}
              yearsOrder="desc"
              onChange={(value) => {
                const now = DateTime.now().setZone(timezone)
                const startTime = value?.startOf('year') ?? timeRange.endTime.startOf('year')
                const endTime = value?.endOf('year') ?? timeRange.endTime

                onChange({
                  startTime,
                  endTime: DateTime.min(endTime, now),
                })
              }}
            />
          </CustomFormControl>
        </LocalizationProvider>
      )
    case 'custom':
      return (
        <CustomFormControl id="revenue-period-custom" variant="outlinedWhite">
          <CustomDateRangePicker
            maxDate={limitDateTimeRange.endTime}
            shouldDisableDate={(date) => date < limitDateTimeRange.startTime.startOf('day')}
            timezone={timezone}
            value={[timeRange.startTime, timeRange.endTime]}
            onAccept={(range) => {
              if (range[0] && range[1]) {
                onChange({
                  startTime: range[0].startOf('day'),
                  endTime: range[1].endOf('day'),
                })
              }
            }}
          />
        </CustomFormControl>
      )
  }
}

export const SiteRevenuePeriodFilter = ({
  onChange,
  timezone,
  defaultPeriod,
  timeRange,
  limitDateTimeRange,
}: Readonly<SiteRevenuePeriodFilterProps>) => {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<Period>(defaultPeriod)

  const periodOptions: SelectFieldOption<Period>[] = [
    { id: 'year', value: 'year', label: t('common.period.year') },
    { id: 'custom', value: 'custom', label: t('common.period.custom') },
  ]

  function getCurrentYearRange(): InsightRevenueDateTimeRange {
    const now = DateTime.now().setZone(timezone)
    const startOfYear = now.startOf('year')

    return {
      startTime: DateTime.max(startOfYear, limitDateTimeRange.startTime),
      endTime: DateTime.min(now, limitDateTimeRange.endTime),
    }
  }

  function handlePeriodChange(evt: SelectChangeEvent) {
    const newPeriod = evt.target.value as Period

    setPeriod(newPeriod)
    onChange(getCurrentYearRange())
  }

  return (
    <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
      <CustomSelectField
        autoWidth
        id="site-revenue-period-filter"
        label={t('common.period.label')}
        options={periodOptions}
        size="medium"
        value={period}
        variant="outlinedWhite"
        onChange={handlePeriodChange}
      />

      <TimeRangeFilterByPeriod
        limitDateTimeRange={limitDateTimeRange}
        period={period}
        timeRange={timeRange}
        timezone={timezone}
        onChange={onChange}
      />
    </Stack>
  )
}
